import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { usd, usdPrecise, shortDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, TrendingUp, TrendingDown, FileSearch } from "lucide-react";

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/invoices").then(r => setItems(r.data));
  }, []);

  const totalQuoted = items.reduce((a, i) => a + i.quoted_usd, 0);
  const totalInvoiced = items.reduce((a, i) => a + i.invoiced_usd, 0);
  const overcharges = items.reduce((a, i) => a + Math.max(0, i.variance_usd), 0);
  const exceptions = items.filter(i => i.status === "EXCEPTION").length;

  return (
    <div className="space-y-6 animate-slide-up" data-testid="invoices-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Invoice Audit</h1>
        <p className="text-sm text-zinc-500 mt-1">Quote vs invoice matching. Catch duplicate charges, invalid surcharges, missing credits.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
        <div className="bg-white p-5">
          <div className="data-label">Total Quoted</div>
          <div className="metric text-3xl mt-2">{usd(totalQuoted)}</div>
        </div>
        <div className="bg-white p-5">
          <div className="data-label">Total Invoiced</div>
          <div className="metric text-3xl mt-2">{usd(totalInvoiced)}</div>
        </div>
        <div className="bg-white p-5 border-l-[3px] border-l-[#DC2626]">
          <div className="data-label">Overcharges Flagged</div>
          <div className="metric text-3xl mt-2 text-[#991B1B]">{usd(overcharges)}</div>
          <div className="text-xs text-zinc-500 mt-2">recoverable</div>
        </div>
        <div className="bg-white p-5 border-l-[3px] border-l-[#F59E0B]">
          <div className="data-label">Invoices in Exception</div>
          <div className="metric text-3xl mt-2">{exceptions}<span className="text-base text-zinc-400">/{items.length}</span></div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-4 border-b border-zinc-200">
          <h3 className="font-heading text-lg font-bold tracking-tight">Invoices</h3>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                {["Invoice #", "Shipment", "Forwarder", "Quoted", "Invoiced", "Variance", "Δ %", "Issued", "Status", "Exceptions", ""].map(h => <th key={h} className="px-4 py-2.5 data-label">{h}</th>)}
              </tr>
            </thead>
            <tbody data-testid="invoices-tbody">
              {items.map(inv => (
                <tr key={inv.id} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                  <td className="px-4 py-2.5 metric font-medium">{inv.id}</td>
                  <td className="px-4 py-2.5 metric text-zinc-700">{inv.shipment_id}</td>
                  <td className="px-4 py-2.5">{inv.forwarder}</td>
                  <td className="px-4 py-2.5 metric">{usdPrecise(inv.quoted_usd)}</td>
                  <td className="px-4 py-2.5 metric">{usdPrecise(inv.invoiced_usd)}</td>
                  <td className="px-4 py-2.5 metric">
                    {inv.variance_usd > 0 ? <span className="text-[#991B1B] inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" />{usdPrecise(inv.variance_usd)}</span> : inv.variance_usd < 0 ? <span className="text-[#166534] inline-flex items-center gap-1"><TrendingDown className="w-3 h-3" />{usdPrecise(Math.abs(inv.variance_usd))}</span> : "—"}
                  </td>
                  <td className="px-4 py-2.5 metric">{inv.variance_pct}%</td>
                  <td className="px-4 py-2.5 metric text-xs text-zinc-600">{shortDate(inv.issued_date)}</td>
                  <td className="px-4 py-2.5"><StatusBadge value={inv.status} /></td>
                  <td className="px-4 py-2.5 text-xs">
                    {inv.exceptions.length > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[#991B1B]"><AlertTriangle className="w-3 h-3" />{inv.exceptions.length}</span>
                    ) : <span className="text-[#166534]">Clean</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <Button
                      data-testid={`audit-btn-${inv.id}`}
                      size="sm" variant="outline"
                      className="rounded-none border-zinc-300 h-8"
                      onClick={() => setActive(inv)}
                    ><FileSearch className="w-3 h-3 mr-1.5" />Audit</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="rounded-none max-w-3xl" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Line-by-line Audit — <span className="metric">{active?.id}</span></DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">Compare every line item from quote to invoice. Flagged exceptions are recoverable.</DialogDescription>
          </DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
                <div className="bg-white p-3"><div className="data-label">Quoted</div><div className="metric text-xl mt-1">{usdPrecise(active.quoted_usd)}</div></div>
                <div className="bg-white p-3"><div className="data-label">Invoiced</div><div className="metric text-xl mt-1">{usdPrecise(active.invoiced_usd)}</div></div>
                <div className="bg-white p-3 border-l-[3px] border-l-[#DC2626]"><div className="data-label">Variance</div><div className="metric text-xl mt-1 text-[#991B1B]">{usdPrecise(active.variance_usd)}</div></div>
              </div>
              <table className="w-full text-sm border border-zinc-200">
                <thead className="bg-zinc-50">
                  <tr className="text-left">
                    {["Code", "Description", "Quoted", "Invoiced", "Δ"].map(h => <th key={h} className="px-3 py-2 data-label">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {active.line_items.map(li => {
                    const d = li.invoiced - li.quoted;
                    return (
                      <tr key={li.code} className="border-t border-zinc-100">
                        <td className="px-3 py-2 metric font-medium">{li.code}</td>
                        <td className="px-3 py-2 text-zinc-700">{li.description}</td>
                        <td className="px-3 py-2 metric">{usdPrecise(li.quoted)}</td>
                        <td className="px-3 py-2 metric">{usdPrecise(li.invoiced)}</td>
                        <td className={`px-3 py-2 metric ${d > 0 ? 'text-[#991B1B]' : d < 0 ? 'text-[#166534]' : 'text-zinc-400'}`}>{d === 0 ? '—' : usdPrecise(d)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {active.exceptions.length > 0 && (
                <div className="border border-[#FCA5A5] bg-[#FEE2E2] p-4">
                  <div className="data-label text-[#991B1B] mb-2">Audit Findings</div>
                  <ul className="space-y-1.5 text-sm">
                    {active.exceptions.map((ex, i) => (
                      <li key={i} className="flex items-start gap-2 text-[#991B1B]">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span><span className="font-semibold">{ex.type.replace(/_/g, ' ')}</span> — {ex.detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
